import type { ColumnDef } from './types.js';

interface ColumnSettingsPanelProps {
  readonly columns: readonly ColumnDef[];
  /**
   * 可供新增的完整欄位目錄（含自訂欄位）。由上層提供 —— 面板不可憑預設目錄自行
   * 產生，否則自訂欄位會少掉 definition。未提供時只能排序與移除。
   */
  readonly availableColumns?: readonly ColumnDef[];
  readonly onColumnsChange: (columns: readonly ColumnDef[]) => void;
  readonly onClose: () => void;
}

/** 欄位設定面板：管理目前顯示哪些欄位、順序，以及從目錄加回未顯示的欄位。 */
export function ColumnSettingsPanel({
  columns,
  availableColumns = [],
  onColumnsChange,
  onClose,
}: ColumnSettingsPanelProps): React.JSX.Element {
  const shownIds = new Set(columns.map((column) => column.id));
  const addableColumns = availableColumns.filter((column) => !shownIds.has(column.id));

  const addColumn = (id: string): void => {
    const column = availableColumns.find((candidate) => candidate.id === id);
    if (column === undefined) return;
    onColumnsChange([...columns, column]);
  };

  const moveColumn = (index: number, direction: -1 | 1): void => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= columns.length) return;
    const next = columns.slice();
    const [moved] = next.splice(index, 1);
    if (moved === undefined) return;
    next.splice(targetIndex, 0, moved);
    onColumnsChange(next);
  };

  const removeColumn = (id: string): void => {
    onColumnsChange(columns.filter((column) => column.id !== id));
  };

  return (
    <div
      data-testid="column-settings-panel"
      className="fixed right-4 top-16 z-50 w-72 rounded-md border border-line bg-surface p-3 text-sm shadow-lg"
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="font-semibold text-fg">欄位設定</span>
        <button
          type="button"
          aria-label="關閉欄位設定"
          className="text-fg-muted hover:text-fg"
          onClick={onClose}
        >
          ×
        </button>
      </div>

      <ul className="mb-3 max-h-64 space-y-1 overflow-y-auto">
        {columns.map((column, index) => (
          <li
            key={column.id}
            className="flex items-center justify-between gap-1 rounded px-1 py-1 hover:bg-raised"
          >
            <span className="truncate text-fg">{column.title}</span>
            <span className="flex shrink-0 gap-1">
              <button
                type="button"
                aria-label={`將 ${column.title} 上移`}
                disabled={index === 0}
                className="disabled:opacity-30"
                onClick={() => moveColumn(index, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                aria-label={`將 ${column.title} 下移`}
                disabled={index === columns.length - 1}
                className="disabled:opacity-30"
                onClick={() => moveColumn(index, 1)}
              >
                ↓
              </button>
              <button
                type="button"
                aria-label={`移除 ${column.title}`}
                className="text-danger hover:opacity-75"
                onClick={() => removeColumn(column.id)}
              >
                ×
              </button>
            </span>
          </li>
        ))}
      </ul>

      {addableColumns.length === 0 ? null : (
        <label className="flex items-center gap-2 border-t border-line pt-2">
          <span className="shrink-0 text-fg-muted">新增欄位</span>
          <select
            data-testid="column-settings-add"
            aria-label="新增欄位"
            className="min-w-0 flex-1 rounded border border-line-strong bg-surface px-1 py-0.5"
            value=""
            onChange={(event) => addColumn(event.target.value)}
          >
            <option value="">選擇欄位…</option>
            {addableColumns.map((column) => (
              <option key={column.id} value={column.id}>
                {column.title}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}
